/**
 * Agent mind (soul + memory) wire contract — spec #42 §5.
 *
 * Consumed by the persona workspace's "Soul & memory" tab (#42 §6), which
 * #44 places. snake_case verbatim, house convention.
 */

/** Where a soul field's current value came from. The vocabulary is step 1's
 * `AgentSettingsResponse.source`, rendered per field. */
export type SoulProvenance = 'default' | 'this_tenant';

export interface SoulLeash {
  allowed_action_types: string[];
  allowed_platforms: string[];
  system_allowed_action_types: string[];
  system_allowed_platforms: string[];
  owned_metrics?: string[];
  owned_tools?: string[];
  /** Floors the tenant cannot cross — rendered locked, never as toggles. */
  locked_fields?: string[];
}

export interface SoulVersionRef {
  label: string;
  at: string | null;
  attribution: string | null;
  by: string | null;
}

export interface SoulVersion {
  label: string;
  at: string | null;
  field: string;
  previous: string | null;
  new: string | null;
  attribution: string | null;
  by: string | null;
  rationale: string | null;
  source_proposal_id: string | null;
}

export interface SoulVersionsResponse {
  items: SoulVersion[];
  total: number;
  has_more: boolean;
}

/** A soul change the agent itself proposed, waiting on the owner. */
export interface SoulPendingProposal {
  proposal_id: string;
  title: string;
  rationale: string | null;
  tier: string;
  created_at: string;
}

export interface SoulResponse {
  domain: string;
  display_name: string;
  charter: string;
  voice: string;
  owned_questions: string[];
  soul_rules: string[];
  leash: SoulLeash;
  /** Per-field provenance: `this_tenant` or `default`, rendered on every
   * field — the tab never shows a value without saying where it came from. */
  provenance: Record<string, SoulProvenance>;
  current_version: SoulVersionRef;
  template: { key: string; version: number | string };
  system_version: number | string | null;
  pending_proposal?: SoulPendingProposal | null;
}

export type MemoryKind = string;

export interface MemoryEntry {
  id: string;
  at: string;
  belief_id: string;
  statement: string;
  kind: MemoryKind;
  kind_class: string;
  confidence_before?: number | null;
  confidence_after: number;
  delta: number;
  sample_size: number | null;
  verified: boolean;
  note: string | null;
  source: string;
  belief_status: string;
  belief_scope: string;
}

export interface MemoryResponse {
  items: MemoryEntry[];
  total: number;
  has_more: boolean;
  /** Never filtered by `kinds=` — the counts describe the whole memory. */
  counts_by_kind: Record<string, number>;
  lanes: { underpowered_available: boolean };
}

/** The hero is either one entry or an explicit reason there is none. */
export interface MemoryHeroEmpty {
  entry: null;
  reason: string;
  beliefs_awaiting_first_test: number;
}

export type MemoryHeroResponse = MemoryEntry | MemoryHeroEmpty;

export function isHeroEmpty(
  hero: MemoryHeroResponse
): hero is MemoryHeroEmpty {
  return (hero as MemoryHeroEmpty).entry === null;
}

/**
 * A payload that really is an entry — checked on its NUMBERS, not on a
 * field name.
 *
 * The hero is rendered as "delta" and "confidence": a body that is neither
 * the entry nor the documented `{entry: null}` empty state (a proxy's
 * error page, a route that answered with the memory LIST, a contract that
 * moved) must draw the unreadable state, not throw inside a render and
 * take the screen down. A browser run caught exactly that.
 */
export function isMemoryEntry(
  hero: MemoryHeroResponse | null | undefined
): hero is MemoryEntry {
  if (!hero || isHeroEmpty(hero as MemoryHeroResponse)) return false;
  const entry = hero as MemoryEntry;
  return (
    typeof entry.delta === 'number' &&
    typeof entry.confidence_after === 'number' &&
    typeof entry.statement === 'string'
  );
}

export interface DecidedItem {
  proposal_id: string;
  title: string;
  status: string;
  decided_at: string | null;
  decision_reason: string | null;
}

export interface DecidedResponse {
  items: DecidedItem[];
  total: number;
  has_more: boolean;
}

/** `GET /api/agents/{domain}/ownership` (#46 §1). */
export interface OwnershipRow {
  tool_key: string;
  display_name: string;
  owner_domain: string;
  /** `false` renders "owned, cannot act yet" — ownership never widens
   * permission, and the UI must not imply that it does. */
  permitted: boolean;
  reason: string | null;
}

export interface OwnershipResponse {
  items: OwnershipRow[];
}
